import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { ProgressBar } from 'primeng/progressbar';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Message } from 'primeng/message';
import { InputText } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { Tooltip } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import {
  Subscription,
  catchError,
  concatMap,
  forkJoin,
  from,
  map,
  of,
  switchMap,
  throwError,
  toArray,
} from 'rxjs';
import { UploadCadComponent } from '../../../../../shared/upload-cad/upload-cad.component';
import { PdfViewerComponent } from '../../../../../shared/pdf-viewer/pdf-viewer.component';
import { LowConfidenceIconComponent } from '../../../../../shared/low-confidence-icon/low-confidence-icon.component';
import { DrawingFileService } from '../../../../../services/drawing-file.service';
import { QuoteService } from '../../../../../quote.service';
import { UseExistingDrawingComponent } from '../../../../../shared/use-existing-drawing/use-existing-drawing.component';
import {
  isInterceptorHandledError,
  parseApiError,
} from '../../../../../quotes-api-error';
import {
  DRAWING_COMPLEXITY_OPTIONS,
  DRAWING_TRIM_EDGE_LENGTH_HELP,
  DRAWING_UPLOAD_ACCEPT,
  DRAWING_UPLOAD_EXTENSIONS_LABEL,
  DrawingExtractionEditForm,
  DrawingFile,
  DrawingSuggestions,
  buildDrawingConfirmPayload,
  customQuoteTreeHasDisplayableRoot,
  drawingAlternateBlueprintLabel,
  drawingExtractionEditFormFromData,
  drawingFilenameStem,
  drawingFormHasRequiredConfirmFields,
  drawingSuggestionMatchLabel,
  drawingTabNeedsReview,
  drawingUploadFileTypeError,
  emptyDrawingExtractionEditForm,
  flattenCustomQuoteTreeForDisplay,
  isDrawingUploadFile,
  isLowDrawingConfidence,
} from '../../../../../../models/drawing-file.model';

type DrawingUploadPhase =
  'select' | 'uploading' | 'processing' | 'completed' | 'failed';

export type ConfirmedDrawingRef = {
  id: number;
  filename: string;
  suggestions?: DrawingSuggestions | null;
};

@Component({
  selector: 'app-quote-item-upload-drawing',
  imports: [
    FormsModule,
    Dialog,
    Button,
    ProgressBar,
    ProgressSpinner,
    Message,
    InputText,
    TextareaModule,
    SelectModule,
    TabsModule,
    Tooltip,
    SharedConfirmDialogComponent,
    LowConfidenceIconComponent,
    UploadCadComponent,
    UseExistingDrawingComponent,
    PdfViewerComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './quote-item-upload-drawing.component.html',
  styleUrl: './quote-item-upload-drawing.component.scss',
})
export class QuoteItemUploadDrawingComponent implements OnDestroy {
  private drawingFileService = inject(DrawingFileService);
  quoteService = inject(QuoteService);
  private confirmationService = inject(ConfirmationService);

  @Input() excludedDrawingIds: number[] = [];
  @Output() closed = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<ConfirmedDrawingRef[]>();

  readonly drawingUploadAccept = DRAWING_UPLOAD_ACCEPT;
  readonly drawingUploadExtensionsLabel = DRAWING_UPLOAD_EXTENSIONS_LABEL;
  readonly complexityOptions = [...DRAWING_COMPLEXITY_OPTIONS];
  readonly trimEdgeLengthHelp = DRAWING_TRIM_EDGE_LENGTH_HELP;

  dialogVisible = true;
  activeSelectionTab: 'upload' | 'existing' = 'upload';
  currentStep = signal(1);
  phase = signal<DrawingUploadPhase>('select');
  fileTypeError = signal('');
  uploadError = signal<string | null>(null);
  processingError = signal<string | null>(null);
  /** All drawings that reached completed extraction (review candidates). */
  drawingFiles = signal<DrawingFile[]>([]);
  /** Per-drawing edit forms keyed by drawing file id. */
  editForms = signal<Record<number, DrawingExtractionEditForm>>({});
  activeDrawingId = signal<number | null>(null);
  activeProcessingFilename = signal<string | null>(null);
  confirmError = signal<string | null>(null);
  confirming = signal(false);
  existingDrawingIds = new Set<number>();

  steps = computed(() => [
    { number: 1, label: 'Upload' },
    { number: 2, label: 'Review' },
  ]);

  private subscriptions = new Subscription();

  activeDrawingFile = computed(() => {
    const id = this.activeDrawingId();
    if (id == null) {
      return null;
    }
    return this.drawingFiles().find((file) => file.id === id) ?? null;
  });

  editForm = computed(() => {
    const id = this.activeDrawingId();
    if (id == null) {
      return emptyDrawingExtractionEditForm();
    }
    return this.editForms()[id] ?? emptyDrawingExtractionEditForm();
  });

  sourceHeader = computed(() => {
    const active = this.activeDrawingFile();
    const count = this.drawingFiles().length;
    if (!active) {
      return '';
    }
    if (count <= 1) {
      return `Source: ${active.filename}`;
    }
    return `Source: ${active.filename} (${count} drawings)`;
  });

  tabsNeedingReviewCount = computed(() => {
    const forms = this.editForms();
    return this.drawingFiles().filter((file) =>
      drawingTabNeedsReview(
        forms[file.id] ?? emptyDrawingExtractionEditForm(),
        file.latest_extraction?.confidence,
      ),
    ).length;
  });

  needsReviewSummary = computed(() => {
    const count = this.tabsNeedingReviewCount();
    if (count === 0) {
      return '';
    }
    if (count === 1) {
      return '1 drawing has fields that need review';
    }
    return `${count} drawings have fields that need review`;
  });

  canConfirm = computed(() => {
    if (this.confirming()) {
      return false;
    }
    const forms = this.editForms();
    const files = this.drawingFiles();
    if (!files.length) {
      return false;
    }
    return files.every(
      (file) =>
        this.isExistingDrawing(file) ||
        drawingFormHasRequiredConfirmFields(
          forms[file.id] ?? emptyDrawingExtractionEditForm(),
        ),
    );
  });

  get confirmButtonLabel(): string {
    return this.existingDrawingIds.size ? 'Use drawing' : 'Confirm dimensions';
  }

  isExistingDrawing(drawing: DrawingFile | null): boolean {
    return drawing !== null && this.existingDrawingIds.has(drawing.id);
  }

  suggestions = computed(
    () => this.activeDrawingFile()?.latest_extraction?.suggestions ?? null,
  );

  suggestionAdvice = computed(() => this.suggestions()?.advice ?? []);

  alternateBlueprintLabel = computed(() =>
    drawingAlternateBlueprintLabel(this.suggestions()?.alternate_blueprints),
  );

  customTree = computed(() => this.suggestions()?.custom_tree ?? null);

  customTreeRows = computed(() =>
    flattenCustomQuoteTreeForDisplay(this.customTree()?.root),
  );

  hasCustomTree = computed(() =>
    customQuoteTreeHasDisplayableRoot(this.customTree()),
  );

  hasSuggestions = computed(() => {
    const suggestions = this.suggestions();
    if (!suggestions) {
      return false;
    }
    return Boolean(
      suggestions.overview?.trim() ||
      suggestions.blueprint?.name ||
      this.alternateBlueprintLabel() ||
      suggestions.advice?.length ||
      this.hasCustomTree(),
    );
  });

  matchLabel(confidence: number): string {
    return drawingSuggestionMatchLabel(confidence);
  }

  /** AI explanation for the extracted complexity class, when present. */
  complexityReason = computed(() => {
    const raw =
      this.activeDrawingFile()?.latest_extraction?.extracted_data?.[
        'complexity_reason'
      ];
    if (raw === null || raw === undefined) {
      return '';
    }
    return String(raw).trim();
  });

  isStepActive(stepNumber: number): boolean {
    return this.currentStep() === stepNumber;
  }

  isStepCompleted(stepNumber: number): boolean {
    return this.currentStep() > stepNumber;
  }

  isLowConfidence(key: string): boolean {
    return isLowDrawingConfidence(
      this.activeDrawingFile()?.latest_extraction?.confidence,
      key,
    );
  }

  tabNeedsReview(drawing: DrawingFile): boolean {
    const form =
      this.editForms()[drawing.id] ?? emptyDrawingExtractionEditForm();
    return drawingTabNeedsReview(form, drawing.latest_extraction?.confidence);
  }

  tabLabel(drawing: DrawingFile): string {
    const form = this.editForms()[drawing.id];
    const title = form?.title?.trim();
    if (title) {
      return title;
    }
    const extractedTitle = stringFromUnknown(
      drawing.latest_extraction?.extracted_data?.['title'],
    );
    if (extractedTitle) {
      return extractedTitle;
    }
    return drawingFilenameStem(drawing.filename);
  }

  onActiveTabChange(value: string | number): void {
    const id = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(id)) {
      return;
    }
    this.activeDrawingId.set(id);
  }

  onFilesSelected(files: File[]): void {
    if (!files?.length) {
      return;
    }

    const invalid = files.filter((file) => !isDrawingUploadFile(file));
    if (invalid.length) {
      this.fileTypeError.set(
        drawingUploadFileTypeError(invalid.map((file) => file.name)),
      );
      return;
    }

    this.fileTypeError.set('');
    this.uploadError.set(null);
    this.processingError.set(null);
    this.phase.set('uploading');
    this.activeProcessingFilename.set(files[0]?.name ?? null);

    const subscription = forkJoin(
      files.map((file) =>
        this.drawingFileService.postDrawingFile$(file).pipe(
          switchMap((drawingFile) => {
            this.phase.set('processing');
            this.activeProcessingFilename.set(drawingFile.filename);
            return this.drawingFileService.pollDrawingFileUntilTerminal(
              drawingFile.id,
              2000,
              300000,
              (progress) => {
                this.activeProcessingFilename.set(progress.filename);
              },
            );
          }),
        ),
      ),
    ).subscribe({
      next: (drawings) => this.handleBatchTerminal(drawings),
      error: (err: unknown) => this.handleBatchError(err),
    });
    this.subscriptions.add(subscription);
  }

  onExistingDrawingsSelected(drawings: DrawingFile[]): void {
    const quoteCompany = this.quoteService.quote.company;
    const hasCompanyMismatch = drawings.some(
      (drawing) => drawing.company !== null && drawing.company !== quoteCompany,
    );
    if (hasCompanyMismatch) {
      this.confirmationService.confirm({
        header: 'Different Customer',
        message:
          'The selected drawing is associated with a different customer than this quote. Are you sure you want to link it?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Link anyway',
        rejectLabel: 'Cancel',
        accept: () => this.handleBatchTerminal(drawings, true),
      });
      return;
    }
    this.handleBatchTerminal(drawings, true);
  }

  addDimensionRow(): void {
    this.updateActiveForm((form) => ({
      ...form,
      dimensions: [...form.dimensions, { key: '', value: '' }],
    }));
  }

  removeDimensionRow(index: number): void {
    this.updateActiveForm((form) => ({
      ...form,
      dimensions: form.dimensions.filter((_, i) => i !== index),
    }));
  }

  updateField<K extends keyof DrawingExtractionEditForm>(
    key: K,
    value: DrawingExtractionEditForm[K],
  ): void {
    this.updateActiveForm((form) => ({ ...form, [key]: value }));
  }

  updateDimensionKey(index: number, key: string): void {
    this.updateActiveForm((form) => ({
      ...form,
      dimensions: form.dimensions.map((row, i) =>
        i === index ? { ...row, key } : row,
      ),
    }));
  }

  updateDimensionValue(index: number, value: string): void {
    this.updateActiveForm((form) => ({
      ...form,
      dimensions: form.dimensions.map((row, i) =>
        i === index ? { ...row, value } : row,
      ),
    }));
  }

  resetToUpload(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.currentStep.set(1);
    this.phase.set('select');
    this.fileTypeError.set('');
    this.uploadError.set(null);
    this.processingError.set(null);
    this.confirmError.set(null);
    this.confirming.set(false);
    this.drawingFiles.set([]);
    this.editForms.set({});
    this.activeDrawingId.set(null);
    this.activeProcessingFilename.set(null);
    this.existingDrawingIds.clear();
    this.activeSelectionTab = 'upload';
  }

  onConfirmDrawing(): void {
    const files = this.drawingFiles();
    if (!files.length || !this.canConfirm()) {
      return;
    }

    const forms = this.editForms();
    for (const file of files) {
      if (this.isExistingDrawing(file)) {
        continue;
      }
      const form = forms[file.id] ?? emptyDrawingExtractionEditForm();
      const { error } = buildDrawingConfirmPayload(form);
      if (error) {
        this.activeDrawingId.set(file.id);
        this.confirmError.set(
          files.length > 1 ? `${this.tabLabel(file)}: ${error}` : error,
        );
        return;
      }
    }

    this.confirmError.set(null);
    this.confirming.set(true);

    const subscription = from(files)
      .pipe(
        concatMap((file) => {
          if (this.isExistingDrawing(file)) {
            return of({
              id: file.id,
              filename: file.filename,
              suggestions: file.latest_extraction?.suggestions ?? null,
            } satisfies ConfirmedDrawingRef);
          }
          const form = forms[file.id] ?? emptyDrawingExtractionEditForm();
          const { payload, error } = buildDrawingConfirmPayload(form);
          if (error || !payload) {
            return throwError(
              () =>
                new Error(
                  files.length > 1
                    ? `${this.tabLabel(file)}: ${error ?? 'Unable to confirm drawing dimensions.'}`
                    : (error ?? 'Unable to confirm drawing dimensions.'),
                ),
            );
          }
          return this.drawingFileService
            .confirmDrawingFile$(file.id, payload)
            .pipe(
              map((updated): ConfirmedDrawingRef => ({
                id: updated.id,
                filename: updated.filename,
                suggestions:
                  updated.latest_extraction?.suggestions ??
                  file.latest_extraction?.suggestions ??
                  null,
              })),
            );
        }),
        toArray(),
        catchError((err: unknown) => {
          if (isInterceptorHandledError(err)) {
            this.confirming.set(false);
            return of([]);
          }
          this.confirming.set(false);
          this.confirmError.set(
            err instanceof Error
              ? err.message
              : parseApiError(err, 'Failed to confirm drawing dimensions.'),
          );
          return of([]);
        }),
      )
      .subscribe({
        next: (confirmed) => {
          if (!confirmed.length) {
            return;
          }
          this.confirming.set(false);
          this.confirmed.emit(confirmed);
          this.dialogVisible = false;
        },
      });
    this.subscriptions.add(subscription);
  }

  onDialogHide(): void {
    this.closed.emit();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateActiveForm(
    updater: (form: DrawingExtractionEditForm) => DrawingExtractionEditForm,
  ): void {
    const id = this.activeDrawingId();
    if (id == null) {
      return;
    }
    this.editForms.update((forms) => ({
      ...forms,
      [id]: updater(forms[id] ?? emptyDrawingExtractionEditForm()),
    }));
  }

  private handleBatchTerminal(
    drawings: DrawingFile[],
    areExistingDrawings = false,
  ): void {
    const failures = drawings.filter((drawing) => drawing.status === 'failed');
    if (failures.length) {
      this.phase.set('failed');
      this.processingError.set(
        failures
          .map(
            (drawing) =>
              `${drawing.filename}: ${
                drawing.error_message?.trim() || 'Drawing extraction failed.'
              }`,
          )
          .join(' '),
      );
      return;
    }

    const forms: Record<number, DrawingExtractionEditForm> = {};
    for (const drawing of drawings) {
      const extracted = drawing.latest_extraction?.extracted_data ?? {};
      forms[drawing.id] = drawingExtractionEditFormFromData(
        extracted,
        drawing.latest_extraction?.confirmed_dimensions,
      );
    }

    this.drawingFiles.set(drawings);
    this.editForms.set(forms);
    this.existingDrawingIds = areExistingDrawings
      ? new Set(drawings.map((drawing) => drawing.id))
      : new Set();
    this.activeDrawingId.set(drawings[0]?.id ?? null);
    this.phase.set('completed');
    this.currentStep.set(2);
  }

  private handleBatchError(err: unknown): void {
    this.phase.set('failed');
    if (isInterceptorHandledError(err)) {
      return;
    }
    this.uploadError.set(
      err instanceof Error
        ? err.message
        : 'Failed to upload or process drawing PDF(s).',
    );
  }
}

function stringFromUnknown(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
}
